package dev.lingvo.site.ds

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

/**
 * The `/ds` gallery's own layout, kept out of the page that uses it.
 *
 * `Ds.kt` is a specimen sheet: it exists to render one of everything the design system can draw, so
 * it grows with the system and there is no version of it that is short. Its scaffolding — the page
 * ground, the two-column card grid, the icon sheet's grid and the two fixed grounds those glyphs
 * are proved against — does not change when a specimen is added, so it lives here and the page is
 * left holding only the specimens themselves.
 *
 * **Nothing here paints a colour from `colorMode`, except the four styles that draw the page.** The
 * icon-sheet grounds deliberately fix their palette to one mode each: their whole job is showing
 * that a glyph reads on paper *and* on ink at the same moment, which a style that follows the
 * reader's theme cannot do — it would show the same answer twice. Those colours are applied at the
 * call site, from `ColorMode.LIGHT.toSitePalette()` and `ColorMode.DARK.toSitePalette()`, so the
 * ladder is still the only source of them.
 */

val DsPageStyle = CssStyle {
    base {
        Modifier
            .minHeight(100.vh)
            .backgroundColor(colorMode.toSitePalette().surfaceBase)
            .color(colorMode.toSitePalette().textPrimary)
            .padding(leftRight = Space.LG)
            .styleModifier { property("padding-block", "${Space.LG} ${Space.Hero}") }
    }
    Breakpoint.MD {
        Modifier.padding(leftRight = Space.XXXL).styleModifier { property("padding-top", Space.XXXL.toString()) }
    }
}

val DsHeroStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .position(Position.Relative)
            // No `minHeight`. It used to ask for 280 on top of `Space.XXXL` padding, so at 1440 the
            // card ran to ~306px while the content ended at ~193 — a 65px band of empty card below
            // the last line, past the point the mosaic's mask has already faded out, with the theme
            // toggle floating in it because `PageHeaderStyle` bottom-aligns its actions. The
            // padding sets the height, which is what the space ladder is for.
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Modal)
            .padding(Space.XL)
            .styleModifier { property("overflow", "hidden") }
    }
    Breakpoint.MD { Modifier.padding(Space.XXXL) }
}

/**
 * The hero's `PageHeader`, raised to display size.
 *
 * The hero used to draw its own `H1`. It now draws the product's, which is the point of rebuilding
 * it on [PageHeader] — the gallery should be showing the component every other page opens with,
 * not a private imitation of it. But a page header's heading is [TypeScale.Heading], and this one
 * is the masthead of the sheet, so the size is raised here rather than by giving `PageHeader` a
 * variant that exists for one caller. The descendant rule is what reaches it: the heading is the
 * component's own element and no modifier a call site passes lands on it.
 */
val DsHeroHeaderStyle = CssStyle {
    cssRule(" h1") {
        Modifier.fontSize(TypeScale.Display).lineHeight(TypeScale.DisplayLineHeight)
    }
}

val DsGalleryStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(Space.XL).galleryGrid("minmax(0, 1fr)") }
    Breakpoint.MD { Modifier.galleryGrid("repeat(2, minmax(0, 1fr))") }
}

val DsSectionStyle = CssStyle {
    base {
        Modifier
            .minWidth(0.px)
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(Radius.Card)
            .padding(Space.LG)
            .styleModifier { property("overflow", "visible") }
    }
    Breakpoint.MD { Modifier.padding(Space.XL) }
}

val DsSpecimenGridStyle = CssStyle {
    base { Modifier.gap(Space.LG).galleryGrid("minmax(0, 1fr)") }
    Breakpoint.MD { Modifier.galleryGrid("repeat(2, minmax(0, 1fr))") }
}

/**
 * A panel whose ground is fixed to one colour mode, whatever the reader's theme says.
 *
 * The colours arrive from the call site because there are two of these on screen at once and only
 * one `colorMode` in a style block.
 */
val DsGroundStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .minWidth(0.px)
        .borderRadius(Radius.Card)
        .padding(Space.LG)
}

/**
 * The icon sheet: as many glyph cells per row as the card is wide enough to hold.
 *
 * The 200px minimum was a desktop number applied to every width. Inside the page padding, the
 * section card and the ground panel a 390px phone has about 246px of track, so exactly one column
 * fitted: 88 cells at ~80px, twice over for paper and ink, is roughly 14,000px of icon sheet —
 * and the fourteen sections under it were all below the fold of a 12,000px capture. The first
 * section of a specimen sheet is not allowed to be the only one a phone can reach.
 *
 * 132px below MD is the track [DsIdentityGridStyle] already uses, so two columns fit; the rung row
 * inside a cell wraps rather than clips. 200px comes back from MD up, where it was right.
 */
val DsIconSheetStyle = CssStyle {
    base { Modifier.gap(Space.MD).galleryGrid("repeat(auto-fill, minmax(132px, 1fr))") }
    Breakpoint.MD { Modifier.galleryGrid("repeat(auto-fill, minmax(200px, 1fr))") }
}

/** One glyph: its size ramp on one line, its upstream `data-lucide` name under it. */
val DsIconCellStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .gap(Space.XS)
        .padding(Space.SM)
        .borderRadius(Radius.Control)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
        }
}

/** The 48 project marks — every icon key against every accent. */
val DsIdentityGridStyle = CssStyle.base {
    Modifier.gap(Space.MD).galleryGrid("repeat(auto-fill, minmax(132px, 1fr))")
}

/** The empty-state glyph set, one cell per [LEmptyStateIcon]. */
val DsGlyphGridStyle = CssStyle.base {
    Modifier.gap(Space.LG).galleryGrid("repeat(auto-fill, minmax(320px, 1fr))")
}

/**
 * Dense packing, so a short cell never leaves a hole beside a tall one.
 *
 * Private and duplicated nowhere: every grid on the gallery wants the same behaviour, and the one
 * that did not have it was the one that left a 570px column of empty colour on the page.
 */
private fun Modifier.galleryGrid(columns: String): Modifier = styleModifier {
    property("grid-auto-flow", "row dense")
    property("display", "grid")
    property("grid-template-columns", columns)
}
